import { Check, Minus } from 'lucide-react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type CheckboxProps = {
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  /** Visible label. Without one, pass `aria-label`. */
  label?: ReactNode;
  /** Secondary line under the label (e.g. why it is disabled). */
  note?: ReactNode;
  'aria-label'?: string;
  disabled?: boolean;
  className?: string;
};

export function Checkbox({ checked, onCheckedChange, label, note, disabled = false, className, ...aria }: CheckboxProps) {
  const id = useId();
  const box = (
    <CheckboxPrimitive.Root
      id={id}
      checked={checked}
      disabled={disabled}
      aria-label={aria['aria-label']}
      onCheckedChange={(next) => { onCheckedChange(next === true); }}
      className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-[4px] border border-line-strong bg-surface text-bg data-[state=checked]:border-fg data-[state=checked]:bg-fg data-[state=indeterminate]:border-fg data-[state=indeterminate]:bg-fg disabled:cursor-not-allowed"
    >
      <CheckboxPrimitive.Indicator>
        {checked === 'indeterminate' ? <Minus className="size-3" aria-hidden="true" /> : <Check className="size-3" strokeWidth={3} aria-hidden="true" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className={cn('grid grid-cols-[16px_1fr] items-start gap-2 rounded-ctl px-1.5 py-[7px] hover:bg-wash', disabled && 'opacity-55', className)}>
      {box}
      <label htmlFor={id} className="text-sm">
        {label}
        {note ? <span className="block text-2xs text-muted">{note}</span> : null}
      </label>
    </div>
  );
}
